import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ErrorHandlerService } from '@y42/error-handling';
import { GoJsSearchNode } from '@y42/gojs';
import { DataLineageNode, DataLineageNodeTypes, LineageSettings } from '@y42/json-schema';
import groupBy from 'lodash/groupBy';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { DataLineageQuery, DataLineageStoreModel, DEFAULT_LINEAGE_SNAPSHOT } from '../../state/data-lineage';
import {
  DataLineageDiagramUIModel,
  DataLineageNodeTypeToLogo,
  DataLineageUINode,
  DependentAsset,
  getUpdatedLineageNodes,
  sanitizeLineageModel,
  UpstreamDownstreamDependentAssets,
} from '../../state/data-lineage-diagram';
import { DataLineageDiagramStoreService } from '../../state/data-lineage-diagram/data-lineage-diagram-store.service';
import { LineageApiService } from './data-lineage-api.service';
import { DataLineageDiagramHydrationService } from './data-lineage-diagram-hydration.service';
import { UpstreamDownstreamDependency } from './lineage-api.model';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class DataLineageDiagramDataService {
  constructor(
    private readonly dataLineageDiagramHydrationService: DataLineageDiagramHydrationService,
    private readonly dataLineageDiagramStoreService: DataLineageDiagramStoreService,
    private readonly dataLineageQuery: DataLineageQuery,
    private readonly lineageApiService: LineageApiService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private readonly loadingUpstreamDownstreamDependencies$$ = new BehaviorSubject<boolean>(false);
  public readonly loadingUpstreamDownstreamDependencies$ = this.loadingUpstreamDownstreamDependencies$$.pipe(
    untilDestroyed(this),
  );

  getLineageModel(snapshotName: DataLineageStoreModel['$name']): Observable<DataLineageDiagramUIModel> {
    this.dataLineageDiagramStoreService.setDataLoading(true);

    const lineage$ =
      snapshotName === DEFAULT_LINEAGE_SNAPSHOT
        ? this.getLatestLineage()
        : this.getAllLineageFromSnapshot(snapshotName);

    return lineage$.pipe(
      tap(() => this.dataLineageDiagramStoreService.setDataLoading(false)),
      tap((model) => this.dataLineageDiagramStoreService.setInputModel(model)),
    );
  }

  getLatestLineage(): Observable<DataLineageDiagramUIModel> {
    return this.lineageApiService.getLatestLineage().pipe(
      map((lineage) => this.removeNodesWithNoChildren(lineage)),
      switchMap((lineageSettings) =>
        this._hydrateGitNodes(lineageSettings.nodeDataArray).pipe(
          map((nodeDataArray) => ({
            linkDataArray: lineageSettings.linkDataArray,
            nodeDataArray,
            visualDataArray: [],
          })),
        ),
      ),
    );
  }

  getPeekLineage(assetPath: string): Observable<DataLineageDiagramUIModel> {
    return this.lineageApiService.getPeakLineage(assetPath).pipe(
      map((lineage) => this.removeNodesWithNoChildren(lineage)),
      switchMap((lineageSettings) =>
        this._hydrateGitNodes(lineageSettings.nodeDataArray).pipe(
          map((nodeDataArray) => ({
            linkDataArray: lineageSettings.linkDataArray,
            nodeDataArray,
            visualDataArray: lineageSettings.visualDataArray,
          })),
        ),
      ),
    );
  }

  getUpstreamAndDownstreamDependencies(assetPath: string): Observable<UpstreamDownstreamDependentAssets> {
    this.loadingUpstreamDownstreamDependencies$$.next(true);
    return this.lineageApiService.getUpstreamAndDownstreamDependencies(assetPath).pipe(
      tap(() => this.loadingUpstreamDownstreamDependencies$$.next(false)),
      switchMap((dependencies) =>
        combineLatest([
          this.hydrateDependencies(dependencies.upstreamNodes),
          this.hydrateDependencies(dependencies.downstreamNodes),
        ]),
      ),
      map(([upstreamNodes, downstreamNodes]) => ({
        upstreamNodes,
        downstreamNodes,
      })),
    );
  }

  getManyUpstreamAndDownstreamDependencies(
    assetPaths: string[],
  ): Observable<Record<string, UpstreamDownstreamDependentAssets>> {
    this.loadingUpstreamDownstreamDependencies$$.next(true);
    return this.lineageApiService.getManyUpstreamAndDownstreamDependencies(assetPaths).pipe(
      tap(() => this.loadingUpstreamDownstreamDependencies$$.next(false)),
      switchMap((dependencies) => {
        const results = dependencies.resultsByKey;
        const mainAssets = Object.keys(results);

        return combineLatest(
          mainAssets.flatMap((mainAsset) => [
            this.hydrateDependencies(results[mainAsset]?.upstreamNodes || []).pipe(
              map((upstreamNodes) => ({ mainAsset, upstreamNodes })),
            ),
            this.hydrateDependencies(results[mainAsset]?.downstreamNodes || []).pipe(
              map((downstreamNodes) => ({ mainAsset, downstreamNodes })),
            ),
          ]),
        );
      }),
      map((mainAssetsWithUpstreamOrDownstreamNodes) => {
        return mainAssetsWithUpstreamOrDownstreamNodes.reduce(
          (acc, val: { mainAsset: string } & Partial<UpstreamDownstreamDependentAssets>) => {
            let currentMainAsset = acc[val.mainAsset];
            if (!currentMainAsset) {
              currentMainAsset = { upstreamNodes: [], downstreamNodes: [] };
            }
            if (val.downstreamNodes) {
              currentMainAsset.downstreamNodes = val.downstreamNodes;
            }
            if (val.upstreamNodes) {
              currentMainAsset.upstreamNodes = val.upstreamNodes;
            }

            acc[val.mainAsset] = currentMainAsset;

            return acc;
          },
          {} as Record<string, UpstreamDownstreamDependentAssets>,
        );
      }),
    );
  }

  setSearchNodes(model: DataLineageDiagramUIModel) {
    const nodes = model.nodeDataArray.filter(
      (node) => !node.isSidebarType && !node.isGroup && node.category !== DataLineageNodeTypes.Empty,
    );

    const nodesMap = new Map<DataLineageUINode['key'], DataLineageUINode>(
      model.nodeDataArray.map((item) => [item.key, item]),
    );

    const searchNodes = nodes.map((node) => {
      const nodeGroup = node.group ? nodesMap.get(node.group) : null;

      const searchNode: GoJsSearchNode = {
        key: node.key,
        entityName: nodeGroup?.name || '',
        entityTypeIcon: nodeGroup?.logo || '',
        subEntityName: node.name || 'Not Found',
        subEntityTypeIcon: node.logo || '',
        nodeType: node.category,
        nodeTypeIcon: DataLineageNodeTypeToLogo[node.category],
      };

      return searchNode;
    });

    this.dataLineageDiagramStoreService.setSearchNodes(searchNodes);
  }

  getRefLineage(assetPath: string): Observable<DataLineageDiagramUIModel> {
    return this.dataLineageDiagramHydrationService.getRefLineage(assetPath).pipe(
      this.errorHandlerService.catchError(
        `[DataLineageRefDiagramComponent]: No node available for the provided key.`,
        () =>
          of({
            linkDataArray: [],
            nodeDataArray: [],
            visualDataArray: [],
          } satisfies DataLineageDiagramUIModel),
        {
          toast: `No node available for the provided key.`,
        },
      ),
    );
  }

  getAssetDependencies(assetPaths: string[]) {
    return this.dataLineageDiagramHydrationService.getAssetDependencies(assetPaths);
  }

  getUpdatedLineageModel(
    currentlyChangedModel: DataLineageDiagramUIModel | null,
    initialModel: DataLineageDiagramUIModel | null,
  ) {
    if (!initialModel || !currentlyChangedModel) {
      return;
    }

    const { linkDataArray } = initialModel;
    const { visualDataArray } = currentlyChangedModel;
    const updatedNodeDataArray = getUpdatedLineageNodes(
      initialModel.nodeDataArray,
      currentlyChangedModel.nodeDataArray,
    );

    const updatedModel: DataLineageDiagramUIModel = {
      linkDataArray,
      nodeDataArray: updatedNodeDataArray,
      visualDataArray,
    };
    return sanitizeLineageModel(updatedModel);
  }

  private hydrateDependencies(dependencies: UpstreamDownstreamDependency[]): Observable<DependentAsset[]> {
    if (dependencies.length === 0) {
      return of([]);
    }

    return combineLatest(
      dependencies.map((dependency) => {
        const hydratedNode$ = this.dataLineageDiagramHydrationService.hydrateGitNode(dependency);
        const children$ = this.hydrateDependencies(dependency.children || []);

        return combineLatest([hydratedNode$, children$]).pipe(
          map(([hydratedNode, children]) => {
            const node: DependentAsset = {
              key: hydratedNode.key,
              name: hydratedNode.name || '',
              logo: hydratedNode.logo || '',
              children,
            };
            return node;
          }),
        );
      }),
    );
  }

  private getAllLineageFromSnapshot(snapshotName: string): Observable<DataLineageDiagramUIModel> {
    return this.dataLineageQuery.selectLoading().pipe(
      filter((dataLineageLoading) => !dataLineageLoading),
      switchMap(() =>
        this.dataLineageQuery.items$.pipe(map((snapshots) => snapshots.find((s) => s.$name === snapshotName))),
      ),
      switchMap((snapshot) => {
        const visualDataArray = snapshot?.visualDataArray || [];
        const entityNodes = (snapshot?.nodeDataArray || []).filter((n) => !n.isSidebarType);
        const linkDataArray = snapshot?.linkDataArray || [];
        const selectedFilters = snapshot?.selectedFilters || [];

        this.dataLineageDiagramStoreService.setFilters(selectedFilters);

        return this._hydrateGitNodes(entityNodes).pipe(
          map((hydratedEntityNodes) => ({
            linkDataArray,
            nodeDataArray: hydratedEntityNodes,
            visualDataArray,
          })),
        );
      }),
    );
  }

  private removeNodesWithNoChildren(model: LineageSettings): LineageSettings {
    const { nodeDataArray } = model;
    const groupChildren = groupBy(nodeDataArray, (n) => n.group);

    const childNodes = nodeDataArray.filter((n) => !n.isGroup);
    const groupNodes = nodeDataArray.filter((n) => n.isGroup && !!groupChildren[n.key]);

    return {
      ...model,
      nodeDataArray: [...groupNodes, ...childNodes],
    };
  }

  private _hydrateGitNodes(nodes: DataLineageNode[]) {
    return this.dataLineageDiagramHydrationService
      .hydrateGitNodes(nodes)
      .pipe(map((hydratedNodes) => hydratedNodes.map((n) => (n.isGroup ? { ...n, isSubGraphExpanded: true } : n))));
  }
}
